/**
 * @fileoverview Promotional Cover Screen Component for 'Rasca y gana' (Scratcher - Tile ID 43) on stadium LED screens (/screen).
 * Renders high-impact promotional branding with sponsor/club logos, continuous floating title animations,
 * dynamic theme backgrounds (color, image, video) with darkness opacity control, and contrast-safe typography.
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (0% CPU usage in idle state).
 * 
 * @module components/Screen/Sections/Scratcher/ScratcherCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme } from '../../../../utilities/BL/Scratcher';

/**
 * Default fallback configuration when theme attributes are missing.
 * @constant {Object}
 */
const DEFAULT_COVER_CONFIGURATION = {
    coverTitle: 'RASCA Y GANA',
    coverBackgroundType: 'image',
    coverBackgroundColor: '#0a0a14',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    brandLogoUrl: '',
    sponsorLogoUrl: '',
    fontFamily: 'Bangers',
    darkness: 45
};

/**
 * ScratcherCover Component
 * 
 * @function ScratcherCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload or initial state from Screen router.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const ScratcherCover = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (
            mediaPath.startsWith('http://') ||
            mediaPath.startsWith('https://') ||
            mediaPath.startsWith('blob:') ||
            mediaPath.startsWith('data:')
        ) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
        return apiBaseEndpointUrl ? `${apiBaseEndpointUrl}${sanitizedMediaPath}` : sanitizedMediaPath;
    }, []);

    /**
     * Fetches the active theme from the backend API if not already provided in payload.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        try {
            const configurationResponse = await GetConfig();
            if (configurationResponse.success && configurationResponse.data) {
                const roomConfiguration = configurationResponse.data;
                if (roomConfiguration.activeTheme) {
                    setActiveThemeData(roomConfiguration.activeTheme);
                } else if (roomConfiguration.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfiguration.activeThemeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }
            }
        } catch (themeFetchError) {
            // Non-blocking fallback to default configuration
        }
    }, []);

    // Synchronize theme when payload changes or trigger initial fetch
    useEffect(() => {
        if (payload) {
            const incomingThemeData =
                payload.state?.activeTheme ||
                payload.state?.currentTheme ||
                payload.data?.activeTheme ||
                payload.data?.currentTheme ||
                payload.payload?.activeTheme ||
                payload.payload?.currentTheme ||
                payload.theme ||
                payload.data?.theme;

            if (incomingThemeData) {
                setActiveThemeData(incomingThemeData);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. EXTRACCIÓN DE ATRIBUTOS Y FALLBACKS
    // =========================================================================
    const coverBackgroundType = activeThemeData?.coverBackgroundType || DEFAULT_COVER_CONFIGURATION.coverBackgroundType;
    const coverBackgroundColor = activeThemeData?.coverBackgroundColor || DEFAULT_COVER_CONFIGURATION.coverBackgroundColor;
    const coverBackgroundUrl = activeThemeData?.coverBackgroundUrl ? resolveMediaUrl(activeThemeData.coverBackgroundUrl) : '';
    const coverTextColor = activeThemeData?.coverTextColor || DEFAULT_COVER_CONFIGURATION.coverTextColor;
    const coverTitleText = activeThemeData?.coverTitle || DEFAULT_COVER_CONFIGURATION.coverTitle;
    const coverPresentsText = activeThemeData?.coverPresentsText || 'PRESENTA';
    const showBrandLogo = activeThemeData?.showBrandLogo !== false;
    const showSponsorLogo = activeThemeData?.showSponsorLogo !== false;
    const showPresentsText = activeThemeData?.showPresentsText !== false;
    const showCoverTitle = activeThemeData?.showCoverTitle !== false;
    const brandLogoUrl = activeThemeData?.brandLogoUrl ? resolveMediaUrl(activeThemeData.brandLogoUrl) : '';
    const sponsorLogoUrl = activeThemeData?.sponsorLogoUrl ? resolveMediaUrl(activeThemeData.sponsorLogoUrl) : '';
    const themeFontFamily = activeThemeData?.fontFamily || DEFAULT_COVER_CONFIGURATION.fontFamily;

    // Darkness slider support (0% to 100% or decimal 0.0 to 1.0)
    const rawDarknessValue = activeThemeData?.darkness ?? activeThemeData?.coverDarkness ?? DEFAULT_COVER_CONFIGURATION.darkness;
    const normalizedDarknessRatio = rawDarknessValue > 1 ? rawDarknessValue / 100 : rawDarknessValue;
    const clampedDarknessRatio = Math.min(0.95, Math.max(0.0, normalizedDarknessRatio));

    // =========================================================================
    // 3. RENDERIZADO DEL FONDO DINÁMICO (ZERO-CANVAS GPU COMPOSITED)
    // =========================================================================
    const renderDynamicBackgroundLayer = () => {
        if (coverBackgroundType === 'video' && coverBackgroundUrl) {
            return (
                <video
                    src={coverBackgroundUrl}
                    className="scratcher-screen-cover__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (coverBackgroundType === 'image' && coverBackgroundUrl) {
            return (
                <div
                    className="scratcher-screen-cover__background-image"
                    style={{ backgroundImage: `url(${coverBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="scratcher-screen-cover__background-color"
                style={{ backgroundColor: coverBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 4. RENDERIZADO PRINCIPAL DE LA PORTADA
    // =========================================================================
    return (
        <main
            className="scratcher-screen-cover"
            role="region"
            aria-label="Portada Promocional Rasca y Gana"
            style={{ fontFamily: themeFontFamily }}
        >
            {/* Capa de Fondo Dinámico */}
            {renderDynamicBackgroundLayer()}

            {/* Capa de Viñeta y Filtro de Oscurecimiento Graduable (Darkness Slider) */}
            <div
                className="scratcher-screen-cover__vignette-overlay"
                style={{
                    backgroundColor: `rgba(6, 6, 16, ${clampedDarknessRatio})`
                }}
            />

            {/* Contenido Central Flotante */}
            <div className="scratcher-screen-cover__content-wrapper">
                {/* Contenedor de Logotipos (Patrocinador & Club / Marca) */}
                {(showSponsorLogo || showBrandLogo) && (
                    <div className="scratcher-screen-cover__logos-container">
                        {showSponsorLogo && sponsorLogoUrl && (
                            <div className="scratcher-screen-cover__logo-box scratcher-screen-cover__logo-box--sponsor">
                                <img
                                    src={sponsorLogoUrl}
                                    alt="Patrocinador Oficial"
                                    className="scratcher-screen-cover__sponsor-logo"
                                />
                            </div>
                        )}

                        {showBrandLogo && (
                            brandLogoUrl ? (
                                <div className="scratcher-screen-cover__logo-box scratcher-screen-cover__logo-box--brand">
                                    <img
                                        src={brandLogoUrl}
                                        alt="Marca / Club"
                                        className="scratcher-screen-cover__brand-logo"
                                    />
                                </div>
                            ) : (
                                <div className="scratcher-screen-cover__logo-box scratcher-screen-cover__logo-box--brand">
                                    <img
                                        src="/img/page/adsl-five.png"
                                        alt="Logotipo Dorados"
                                        className="scratcher-screen-cover__brand-logo"
                                    />
                                </div>
                            )
                        )}
                    </div>
                )}

                {/* Subtítulo Presenta */}
                {showPresentsText && (
                    <span
                        className="scratcher-screen-cover__presenta-badge"
                        style={{ color: coverTextColor }}
                    >
                        {coverPresentsText}
                    </span>
                )}

                {/* Título Principal de la Dinámica con Flotación Continua */}
                {showCoverTitle && (
                    <h1
                        className="scratcher-screen-cover__hero-title"
                        style={{ color: coverTextColor }}
                    >
                        {coverTitleText}
                    </h1>
                )}
            </div>

            {/* Pie de Página Corporativo con Créditos */}
            <footer
                className="scratcher-screen-cover__footer-credits"
                style={{ color: coverTextColor, fontSize: '1.6em' }}
            >
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

ScratcherCover.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    })
};

ScratcherCover.defaultProps = {
    payload: null
};
